import type { ReactNode } from 'react';
import type { Printer, QzPrinterDetails, PrinterRuntimeStatus } from '@features/printer/types';

import qz from 'qz-tray';
import ipRegex from 'ip-regex';
import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import { useAppDispatch } from '@core/store/hooks';

import localIp from '@shared/utils/local-ip';
import { FatalError } from '@shared/components/fatal-error';
import { SplashScreen } from '@shared/components/splash-screen';

import { usePrinterListQuery } from '@features/printer/api/printer-api';
import { useComputerExistQuery } from '@features/printer/api/computer-api';
import { setPrintJobReporter } from '@features/printer/utils/print-handler';
import { addNewLog, updateLog } from '@features/printer/slices/printer-log-slice';

type PrinterContextType = {
  isComputerExist: boolean;
  printerList: Printer[];
  qz: typeof qz;
  ip: string; // On s'assure que l'IP ne sera jamais null dans le contexte fourni
  isQzReady: boolean;
  /** Printers installed on this computer, as seen by QZ Tray (empty while QZ Tray is unreachable) */
  qzPrinters: QzPrinterDetails[];
  /** Last status event received per printer OS name */
  printerStatuses: Record<string, PrinterRuntimeStatus>;
  /** (Re)connects to QZ Tray and reloads the installed printers */
  reconnectQz: () => Promise<void>;
};

const PrinterContext = createContext<PrinterContextType | null>(null);

export const PrinterProvider = ({ children }: { children: ReactNode }) => {
  const { isLoading, isSuccess } = useComputerExistQuery();
  const { data: printerList = [], isLoading: isLoadingPrinters, isError, error } = usePrinterListQuery();
  const [isQzReady, setIsQzReady] = useState(qz.websocket.isActive());
  const [ip, setIp] = useState<string | null>(null);
  const [qzPrinters, setQzPrinters] = useState<QzPrinterDetails[]>([]);
  const [printerStatuses, setPrinterStatuses] = useState<Record<string, PrinterRuntimeStatus>>({});
  const dispatch = useAppDispatch();

  // Every print job reports its outcome (sent / failed, duration) to the history.
  useEffect(() => {
    setPrintJobReporter({
      create: (log) => {
        dispatch(addNewLog(log));
      },
      update: (id, patch) => {
        dispatch(updateLog({ id, patch }));
      },
    });
    return () => setPrintJobReporter(null);
  }, [dispatch]);

  // Installed printers + live status events. Status wording depends on the OS (Windows spooler, CUPS...):
  // it is displayed as received and only classified loosely (see classifyPrinterStatus).
  const loadQzPrinters = useCallback(async () => {
    try {
      const details = await qz.printers.details();
      const list = (Array.isArray(details) ? details : [details]) as QzPrinterDetails[];
      setQzPrinters(list.filter((item) => item && typeof item.name === 'string'));
    } catch (err) {
      console.error('Impossible de lister les imprimantes QZ Tray:', err);
      setQzPrinters([]);
    }
  }, []);

  const listenToPrinterStatus = useCallback(async () => {
    try {
      const printers = qz.printers as any;
      printers.setPrinterCallbacks((event: { printerName?: string; statusText?: string; status?: string }) => {
        const name = event.printerName;
        const status = event.statusText ?? event.status;
        if (name && status) {
          setPrinterStatuses((current) => ({ ...current, [name]: { status, receivedAt: Date.now() } }));
        }
      });
      await printers.startListening(null);
    } catch (err) {
      console.error("Impossible d'écouter le statut des imprimantes:", err);
    }
  }, []);

  const reconnectQz = useCallback(async () => {
    try {
      if (!qz.websocket.isActive()) {
        await qz.websocket.connect();
      }
      setIsQzReady(true);
      await loadQzPrinters();
      await listenToPrinterStatus();
    } catch (err) {
      console.error('Erreur de connexion à QZ Tray:', err);
      setIsQzReady(false);
      setQzPrinters([]);
    }
  }, [loadQzPrinters, listenToPrinterStatus]);

  // Utiliser useEffect pour les effets de bord (connexion, fetch IP)
  useEffect(() => {
    // 1. Connexion à QZ Tray
    const connectQz = async () => {
      await reconnectQz();
    };

    // The connection can drop later (QZ Tray restarted): reflect it instead of staying "ready".
    // @ts-ignore
    qz.websocket.setClosedCallbacks(() => {
      setIsQzReady(false);
      setQzPrinters([]);
    });

    // 2. Récupération de l'IP locale
    const getIp = async () => {
      try {
        const currentIP = await localIp();
        if (currentIP) {
          setIp(currentIP);
        } else {
          // Gérer le cas où l'IP n'est pas récupérée
          console.error("Impossible de récupérer l'IP locale");
          setIp('error'); // ou une autre valeur pour indiquer l'échec
        }
      } catch (err) {
        console.error("Erreur lors de la récupération de l'IP locale:", err);
        setIp('error');
      }
    };

    connectQz();
    getIp();

    // La fonction de nettoyage de useEffect est utile si QZ Tray a une méthode de déconnexion
    return () => {
      if (qz.websocket.isActive()) {
        qz.websocket.disconnect(); // Si une telle fonction existe
      }
    };
    // Connect once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La valeur du contexte est mémoïsée pour éviter les re-rendus inutiles des consommateurs
  const value = useMemo(
    () => ({
      isComputerExist: isSuccess,
      printerList,
      qz,
      ip: ip!, // On est certain que `ip` ne sera pas `null` après le check de chargement
      isQzReady,
      qzPrinters,
      printerStatuses,
      reconnectQz,
    }),
    [printerList, ip, isQzReady, isSuccess, qzPrinters, printerStatuses, reconnectQz]
  );

  // On attend que TOUTES les données essentielles soient chargées
  if (isLoadingPrinters || ip === null || isLoading) {
    return <SplashScreen />;
  }

  // En cas d'erreur critique

  // @ts-expect-error
  if ((isError && error.status !== 404) || ip === 'error') {
    return <FatalError message="Impossible de charger les paramètres d'impression." />;
  }

  return <PrinterContext.Provider value={value}>{children}</PrinterContext.Provider>;
};

type UsePrinterType = PrinterContextType & {
  isIPv4: boolean;
};

// Le hook `usePrinter` reste presque inchangé, mais on peut améliorer son type de retour
export const usePrinter = (): UsePrinterType => {
  const context = useContext(PrinterContext);

  if (context === null) {
    throw new Error("usePrinter() doit être utilisé à l'intérieur d'un PrinterProvider");
  }

  return {
    ...context,
    isIPv4: ipRegex({ exact: true }).test(context.ip),
  };
};
